import React from 'react';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { DecisionActionId, DecisionItem } from '../../../common/hooks/use-decisions-api.hook';
import { findingText, relativeTime } from '../insights/insight-presentation';
import { SOURCE_ICON, actionLabelKey, formatMoney, rowActions, sourceKey } from './decision-presentation';

/** The picture of the thing — a render, a post — or the source's glyph when there is none. */
export const DecisionThumb: React.FC<{ item: Pick<DecisionItem, 'media' | 'source'>; className?: string }> = ({ item, className }) => {
  const Icon = SOURCE_ICON[item.source];
  return item.media ? (
    <img src={item.media} alt="" loading="lazy" className={cn('size-14 shrink-0 rounded-lg border object-cover', className)} />
  ) : (
    <span className={cn('flex size-14 shrink-0 items-center justify-center rounded-lg border bg-muted/40 text-muted-foreground', className)}>
      <Icon className="size-5" aria-hidden />
    </span>
  );
};

/** A row's title in the reader's language: a detector's finding is keyed, everything else arrives worded. */
export const decisionTitle = (t: (key: string, opts?: any) => string, item: DecisionItem) =>
  item.i18n?.title ? findingText(t, item.title, { key: item.i18n.title, params: item.i18n.params }) : item.title;

export const decisionDetail = (t: (key: string, opts?: any) => string, item: DecisionItem) =>
  item.i18n?.detail ? findingText(t, item.detail, { key: item.i18n.detail, params: item.i18n.params }) : item.detail;

/**
 * One decision: what it is, what it is worth, and the buttons to settle it
 * where it stands. "Open" goes to the page that owns it.
 */
export const DecisionRow: React.FC<{
  item: DecisionItem;
  busy: string | null;
  highlighted?: boolean;
  onAct: (item: DecisionItem, action: DecisionActionId) => void;
}> = ({ item, busy, highlighted, onAct }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').slice(0, 2);
  const Icon = SOURCE_ICON[item.source];
  const { primary, secondary, openIsPrimary } = rowActions(item);
  const value = formatMoney(item.value, lang);
  const ago = relativeTime(item.createdAt);
  const detail = decisionDetail(t as any, item);
  const pending = (a: DecisionActionId) => busy === `${item.id}:${a}`;
  const disabled = !!busy && busy.startsWith(`${item.id}:`);

  // The yes is an ink outline, like the Work board's decision cards: a column
  // of solid black buttons down an inbox reads as shouting.
  const button = (a: DecisionActionId, variant: 'default' | 'outline' | 'ghost') => (
    <Button
      key={a}
      size="sm"
      variant={variant === 'default' ? 'outline' : variant}
      disabled={disabled}
      onClick={() => onAct(item, a)}
      className={cn(variant === 'default' && 'border-foreground', variant === 'outline' && 'text-muted-foreground', variant === 'ghost' && 'text-muted-foreground')}
      data-testid={`decision-${a}`}
    >
      {pending(a) && <Loader2 className="animate-spin" />}
      {t(actionLabelKey(item, a))}
      {a === 'open' && <ArrowUpRight data-icon="inline-end" className="rtl:-scale-x-100" />}
    </Button>
  );

  return (
    <li
      id={`decision-${item.id}`}
      data-testid="decision-row"
      data-source={item.source}
      className={cn('flex flex-col gap-3 p-4 transition-colors sm:flex-row sm:items-center', highlighted && 'bg-muted/50', disabled && 'opacity-70')}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <DecisionThumb item={item} />
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon className="size-3.5 shrink-0" aria-hidden />
            <span className="truncate">{t(sourceKey(item.source))}</span>
            <span aria-hidden>·</span>
            <span className="shrink-0">{t(ago.key, { count: ago.count })}</span>
          </p>
          <p className="mt-0.5 line-clamp-2 text-sm font-medium leading-snug">{decisionTitle(t as any, item)}</p>
          {detail && <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{detail}</p>}
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1.5 ps-[68px] sm:ps-0">
        {value && <span className="me-2 text-sm font-medium tabular-nums">{value}</span>}
        {openIsPrimary && button('open', 'default')}
        {primary.map((a) => button(a, 'default'))}
        {secondary.map((a) => button(a, 'outline'))}
        {!openIsPrimary && button('open', 'ghost')}
      </div>
    </li>
  );
};
